/**
 * Old (Vue) -> new (Next.js) design switch: "classic -> new" direction.
 *
 * Writes the `gs_handoff` cookie described in profile-new's
 * docs/DESIGN-SWITCH.md and redirects to the new app's /design-switch
 * route, which reads the cookie, adopts the tokens into its own
 * localStorage and deletes the cookie. See that doc for the full
 * contract, threat model and the "new -> classic" direction.
 *
 * Token storage format (`{type, value, expiration}`, unix seconds) is
 * shared between both apps and documented in docs/AUTH-FLOW.md on the
 * profile-new side; see src/utils/tokens.js for the Vue-side reader.
 */

const HANDOFF_COOKIE_NAME = "gs_handoff";
const HANDOFF_MAX_AGE_SECONDS = 60;
const DEFAULT_NEW_DESIGN_URL = "https://edu-new.genius.space";

function stripTrailingSlash(url) {
	return typeof url === "string" ? url.replace(/\/+$/, "") : url;
}

export const NEW_DESIGN_URL = stripTrailingSlash(
	process.env.VUE_APP_NEW_DESIGN_URL || DEFAULT_NEW_DESIGN_URL
);

/**
 * @param {string} pathname e.g. "/uk/courses"
 * @returns {'uk'|'en'}
 */
export function detectLocaleFromPath(pathname) {
	if (typeof pathname === "string") {
		const match = pathname.match(/^\/(en|uk)(\/|$)/);
		if (match) {
			return match[1];
		}
	}

	return "uk";
}

/**
 * @param {string} localStorageKey e.g. "authorization_token"
 * @param {number} nowSeconds
 * @returns {{type: string, token: string, expiration: number}|null}
 */
function readValidToken(localStorageKey, nowSeconds) {
	let raw;
	try {
		raw = localStorage.getItem(localStorageKey);
	} catch (e) {
		return null;
	}

	if (!raw) {
		return null;
	}

	let parsed;
	try {
		parsed = JSON.parse(raw);
	} catch (e) {
		return null;
	}

	if (!parsed || !parsed.type || !parsed.value || !parsed.expiration) {
		return null;
	}

	const expiration = Number(parsed.expiration);

	if (!Number.isFinite(expiration) || expiration <= nowSeconds) {
		return null;
	}

	return {
		type: parsed.type,
		token: parsed.value,
		expiration,
	};
}

/**
 * Reads this app's own localStorage tokens and builds the gs_handoff
 * payload. Returns null when there is no valid (non-expired) token at all
 * — nothing to hand off in that case.
 *
 * @returns {object|null}
 */
export function buildHandoffPayload() {
	const nowSeconds = Math.floor(Date.now() / 1000);

	const tokens = [
		readValidToken("authorization_token", nowSeconds),
		readValidToken("refresh_token", nowSeconds),
	].filter(Boolean);

	if (tokens.length === 0) {
		return null;
	}

	let sessionId;
	try {
		sessionId = localStorage.getItem("session-id") || undefined;
	} catch (e) {
		sessionId = undefined;
	}

	const payload = {
		v: 1,
		tokens,
		iat: nowSeconds,
		locale: detectLocaleFromPath(window.location.pathname),
	};

	if (sessionId) {
		payload.sessionId = sessionId;
	}

	return payload;
}

/**
 * `.genius.space` on real *.genius.space hosts (so the cookie is visible on
 * both edu.genius.space and edu-new.genius.space), host-only cookie
 * (no Domain attribute) everywhere else (localhost, tests, previews).
 *
 * @param {string} hostname
 * @returns {string|null}
 */
export function cookieDomainFor(hostname) {
	if (typeof hostname === "string" && hostname.endsWith("genius.space")) {
		return ".genius.space";
	}

	return null;
}

/**
 * @param {object} opts
 * @param {string} opts.name
 * @param {string} opts.value already-encoded cookie value
 * @param {number} opts.maxAge seconds
 * @param {string|null} [opts.domain]
 * @param {boolean} [opts.secure]
 * @returns {string} value ready to assign to document.cookie
 */
export function buildCookieString({ name, value, maxAge, domain, secure }) {
	let cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;

	if (domain) {
		cookie += `; Domain=${domain}`;
	}

	if (secure) {
		cookie += "; Secure";
	}

	return cookie;
}

function writeHandoffCookie(payload) {
	const value = encodeURIComponent(JSON.stringify(payload));
	const domain = cookieDomainFor(window.location.hostname);
	const secure = window.location.protocol === "https:";

	document.cookie = buildCookieString({
		name: HANDOFF_COOKIE_NAME,
		value,
		maxAge: HANDOFF_MAX_AGE_SECONDS,
		domain,
		secure,
	});
}

/**
 * Appends a flat query object onto a URL, preserving any query string that is
 * already there. Empty/null/undefined values are skipped. Keys and values are
 * URL-encoded. Returns the URL unchanged when there is nothing to append.
 *
 * @param {string} url
 * @param {Object<string,(string|number)>} [query]
 * @returns {string}
 */
export function appendQuery(url, query) {
	if (!query || typeof query !== "object") {
		return url;
	}

	const pairs = Object.keys(query)
		.filter((key) => query[key] !== undefined && query[key] !== null && query[key] !== "")
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`);

	if (pairs.length === 0) {
		return url;
	}

	const separator = url.indexOf("?") === -1 ? "?" : "&";

	return `${url}${separator}${pairs.join("&")}`;
}

/**
 * Entry point for the "Try new design" actions (dropdown item + migration
 * banner). Writes the gs_handoff cookie from this app's live tokens and does a
 * full navigation to the new app's /design-switch route. When there is nothing
 * valid to hand off (no live token at all), navigates straight to the new app's
 * /login — it will simply ask the user to log in there.
 *
 * @param {Object} [options]
 * @param {Object<string,(string|number)>} [options.query] extra query params
 *   (e.g. UTM tags) appended to the destination URL — on both the
 *   /design-switch and the /login fallback, so campaign attribution survives
 *   the "no live token" case.
 */
export function switchToNewDesign(options) {
	const query = options && options.query;
	const payload = buildHandoffPayload();

	if (!payload) {
		window.location.href = appendQuery(`${NEW_DESIGN_URL}/login`, query);
		return;
	}

	writeHandoffCookie(payload);

	window.location.href = appendQuery(`${NEW_DESIGN_URL}/design-switch`, query);
}
